import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Building2, 
  ChevronDown, 
  Check, 
  Search, 
  Shield, 
  Eye,
  AlertTriangle,
  Clock
} from 'lucide-react';
import { authService, type WorkspaceAccess } from '../services/authService';

interface WorkspaceSwitcherProps {
  className?: string;
}

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const currentWorkspace = authService.getCurrentWorkspace();
  const workspaces = authService.getWorkspaces();
  const account = authService.getCurrentAccount();

  const filteredWorkspaces = workspaces.filter(workspace =>
    workspace.organizationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    workspace.organizationId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSwitchWorkspace = async (organizationId: string) => {
    if (switching || organizationId === currentWorkspace?.organizationId) return;

    setSwitching(true);
    try {
      await authService.switchWorkspace(organizationId);
      setIsOpen(false);
      setSearchQuery('');
      
      // Update URL to use new organization ID
      const currentPath = location.pathname;
      const currentOrgMatch = currentPath.match(/^\/([a-zA-Z0-9_]+)(.*)/);
      
      if (currentOrgMatch) {
        // Replace org ID in current path
        const basePath = currentOrgMatch[2] || '/';
        navigate(`/${organizationId}${basePath}`);
      } else {
        // Navigate to new org's dashboard
        navigate(`/${organizationId}/`);
      }
    } catch (error) {
      console.error('Failed to switch workspace:', error);
      // You could show a toast notification here
    } finally {
      setSwitching(false);
    }
  };

  const getAccessLevelIcon = (level: string) => {
    switch (level) {
      case 'admin': return <Shield className="w-4 h-4 text-red-500" />;
      case 'write': return <Shield className="w-4 h-4 text-yellow-500" />;
      case 'read': return <Eye className="w-4 h-4 text-blue-500" />;
      default: return <Eye className="w-4 h-4 text-gray-500" />;
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'admin': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      case 'write': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      case 'read': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
      default: return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  if (!account || workspaces.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`relative w-full ${className}`}>
        {/* Current Workspace Display */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-3 w-full p-3 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors"
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <Building2 className="w-5 h-5 text-primary-500 flex-shrink-0" />
            <div className="text-left min-w-0 flex-1">
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary truncate">
                {currentWorkspace?.organizationName || 'No Workspace Selected'}
              </p>
              <div className="flex items-center space-x-2 min-w-0">
                <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary truncate flex-shrink">
                  {currentWorkspace?.organizationId || '—'}
                </span>
                {currentWorkspace && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${getAccessLevelColor(currentWorkspace.accessLevel)}`}>
                    {currentWorkspace.accessLevel}
                  </span>
                )}
              </div>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown - Positioned relative to container */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg shadow-xl z-50 overflow-hidden max-h-96">
            {/* Admin Info */}
            <div className="p-4 border-b border-light-border dark:border-dark-border bg-primary-50 dark:bg-primary-900/20">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-primary-500" />
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  Admin: {account.firstName} {account.lastName}
                </span>
              </div>
              <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                Troubleshooting access to customer workspaces
              </p>
            </div>

            {/* Search */}
            <div className="p-3 border-b border-light-border dark:border-dark-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workspaces..."
                  className="w-full pl-9 pr-3 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded text-sm text-light-text-primary dark:text-dark-text-primary placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Workspace List */}
            <div className="max-h-48 overflow-y-auto">
              {filteredWorkspaces.length === 0 ? (
                <div className="p-4 text-center text-light-text-secondary dark:text-dark-text-secondary">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No workspaces found</p>
                </div>
              ) : (
                filteredWorkspaces.map((workspace) => {
                  const isActive = workspace.organizationId === currentWorkspace?.organizationId;
                  const isExpiring = workspace.expiresAt && new Date(workspace.expiresAt) < new Date(Date.now() + 24 * 60 * 60 * 1000);
                  
                  return (
                    <button
                      key={workspace.id}
                      onClick={() => handleSwitchWorkspace(workspace.organizationId)}
                      disabled={switching || isActive}
                      className={`w-full p-4 text-left hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors border-b border-light-border dark:border-dark-border last:border-b-0 ${
                        isActive ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      } ${switching ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                              {workspace.organizationName}
                            </h4>
                            {isActive && <Check className="w-4 h-4 text-primary-500" />}
                          </div>
                          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mb-2">
                            {workspace.organizationId}
                          </p>
                          <div className="flex items-center space-x-2">
                            {getAccessLevelIcon(workspace.accessLevel)}
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getAccessLevelColor(workspace.accessLevel)}`}>
                              {workspace.accessLevel} access
                            </span>
                            {isExpiring && (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium text-orange-600 bg-orange-50 dark:bg-orange-900/20 flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>Expiring soon</span>
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                            Reason: {workspace.reason}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-light-border dark:border-dark-border bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary text-center">
                All workspace switches are logged for security audit
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-25" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};